import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Panel } from "primereact/panel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import MeaButton from "../../../../components/MeaButton";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import tasksAPI from "../../../../services/tasks";
import { ToFormatedDateWithWeekday, ToFormatedHour } from "../../../../utils/dates";

const ImportSlotChangeRequestsModal = forwardRef((props, ref) => {
  const { auth } = useAuthContext();
  const { schoolId, trainingId, onImported } = props;

  const [active, setActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(-1);
  const [jobMessage, setJobMessage] = useState(null);
  const [jobLogs, setJobLogs] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const logSummary = (jobLogs || []).reduce(
    (acc, item) => {
      const type = item?.log_type;
      const message = (item?.message || "").toLowerCase();
      if (type === "ERROR") {
        acc.errors += 1;
      } else if (type === "WARNING") {
        acc.warnings += 1;
      } else if (type === "INFO" && message.includes("demande créée")) {
        acc.created += 1;
      }
      return acc;
    },
    { errors: 0, warnings: 0, created: 0 }
  );

  const resetState = () => {
    setFile(null);
    setUploading(false);
    setJobId(null);
    setJobProgress(-1);
    setJobMessage(null);
    setJobLogs([]);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  useImperativeHandle(ref, () => ({
    show: () => {
      resetState();
      setActive(true);
    },
  }));

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const blob = await slotChangeRequestsAPI.DownloadImportTemplate(
        schoolId,
        trainingId,
        auth?.access_token || ""
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "modele-demandes-changement-creneau.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(err.message || "Erreur de téléchargement du modèle d'import");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }
    setUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setJobMessage("Envoi du fichier...");
    try {
      const response = await slotChangeRequestsAPI.ImportFromExcelFile(
        schoolId,
        trainingId,
        auth?.access_token || "",
        file,
        file.name
      );
      setJobId(response?.data?.id || null);
      setJobProgress(response?.data?.progress ?? -1);
    } catch (err) {
      setUploading(false);
      setJobId(null);
      setErrorMessage(err.message || "Erreur d'importation du fichier");
    }
  };

  useEffect(() => {
    if (!uploading || !jobId) {
      return;
    }

    let cancelled = false;
    const pollTask = () => {
      tasksAPI
        .GetTask(schoolId, trainingId, jobId, auth?.access_token || "")
        .then((data) => {
          if (cancelled) {
            return;
          }
          const task = data?.data;
          setJobMessage(task?.message || null);
          setJobProgress(task?.progress ?? -1);
          setJobLogs(task?.job_logs || []);

          if (task?.status === "COMPLETED") {
            setUploading(false);
            setJobId(null);
            setSuccessMessage(task?.message || "Import terminé");
            if (onImported) {
              onImported();
            }
            return;
          }
          if (task?.status === "FAILED") {
            setUploading(false);
            setJobId(null);
            setErrorMessage(task?.message || "Échec de l'import");
            return;
          }

          setTimeout(pollTask, 1000);
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          setUploading(false);
          setJobId(null);
          setErrorMessage(err?.message || "Erreur de suivi du job d'import");
        });
    };

    pollTask();
    return () => {
      cancelled = true;
    };
  }, [uploading, jobId, schoolId, trainingId, auth?.access_token, onImported]);

  return (
    <Dialog
      header="Importer des demandes depuis un fichier Excel"
      visible={active}
      onHide={() => {
        setActive(false);
        resetState();
      }}
      style={{ width: "42rem", maxWidth: "95vw" }}
    >
      <div className="flex flex-column gap-3">
        <div className="text-700">
          Format attendu (colonnes en Français): <strong>Élève (Nom Prénom)</strong>{" "}
          (informatif), <strong>Immatriculation élève</strong>,{" "}
          <strong>Date de la demande</strong>, <strong>Urgence</strong>,{" "}
          <strong>Motif</strong>, <strong>Créneau souhaité 1</strong>,{" "}
          <strong>Créneau souhaité 2</strong>, <strong>Créneau souhaité 3</strong>.
          <br />
          Les colonnes <strong>Urgence</strong> et <strong>Créneaux souhaités</strong> sont
          remplies via listes déroulantes dans le modèle.
        </div>
        <div>
          <MeaButton
            outlined
            size="small"
            icon="pi pi-download"
            label="Télécharger un modèle"
            onClick={handleTemplateDownload}
          />
        </div>

        {!uploading && !successMessage ? (
          <input type="file" accept=".xlsx,.xlsm" onChange={handleFileChange} />
        ) : null}

        {file && !uploading && !successMessage ? (
          <small className="text-600">
            Fichier: {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </small>
        ) : null}

        {uploading ? (
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-spin pi-cog" />
              <span>{jobMessage || "Import en cours..."}</span>
            </div>
            <ProgressBar
              mode={jobProgress < 0 ? "indeterminate" : "determinate"}
              value={jobProgress < 0 ? undefined : jobProgress}
              style={{ height: "10px" }}
            />
          </div>
        ) : null}

        {errorMessage ? <Message severity="error" text={errorMessage} /> : null}
        {successMessage ? <Message severity="success" text={successMessage} /> : null}

        {jobLogs.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <div className="border-round bg-green-50 text-green-700 px-2 py-1 text-sm font-medium">
              {logSummary.created} création(s)
            </div>
            <div className="border-round bg-orange-50 text-orange-700 px-2 py-1 text-sm font-medium">
              {logSummary.warnings} avertissement(s)
            </div>
            <div className="border-round bg-red-50 text-red-700 px-2 py-1 text-sm font-medium">
              {logSummary.errors} erreur(s)
            </div>
          </div>
        ) : null}

        {jobLogs.length > 0 ? (
          <Panel header="Détails de l'import" toggleable>
            <DataTable value={jobLogs} size="small" responsiveLayout="scroll">
              <Column
                field="log_type"
                header="Type"
                body={(item) => (
                  <span
                    className={
                      item?.log_type === "ERROR"
                        ? "text-red-600 font-medium"
                        : item?.log_type === "WARNING"
                          ? "text-orange-500 font-medium"
                          : "text-700"
                    }
                  >
                    {item?.log_type}
                  </span>
                )}
                style={{ width: "7rem" }}
              />
              <Column field="message" header="Message" />
              <Column
                header="Fait le"
                body={(item) =>
                  item?.created_at
                    ? `${ToFormatedDateWithWeekday(new Date(item.created_at))} ${ToFormatedHour(new Date(item.created_at))}`
                    : "-"
                }
                style={{ width: "14rem" }}
              />
            </DataTable>
          </Panel>
        ) : null}

        <div className="flex justify-content-end gap-2 mt-2">
          <MeaButton
            outlined
            label={successMessage || errorMessage ? "Fermer" : "Annuler"}
            onClick={() => {
              setActive(false);
              resetState();
            }}
          />
          {!successMessage && !errorMessage ? (
            <MeaButton
              label="Importer"
              icon="pi pi-upload"
              disabled={!file || uploading}
              onClick={handleUpload}
            />
          ) : null}
        </div>
      </div>
    </Dialog>
  );
});

export default ImportSlotChangeRequestsModal;
